import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// Import of the module
import { parseDate } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

// Type of the fields in the table
export type Country = {
  id: number;
  code: string;
  economical_zone: number | null;
  market_type: number | null;
  continent: number | null;
  market_group: number | null;
  en_translation: string | null;
  fr_translation: string | null;
  currency: string | null;
  emu_oecd_zone: number | null;
  created_at: string;
  updated_at: string;
};

// Enum mappings for display
const ECONOMICAL_ZONE_MAP: Record<number, string> = {
  0: "Alena",
  1: "Asean",
  2: "Brics",
  3: "EEA Ex Eurozone",
  4: "Other",
  5: "Eurozone",
};

const MARKET_TYPE_MAP: Record<number, string> = {
  0: "Developed Markets",
  1: "Emerging Markets",
  2: "Frontier Markets",
  3: "Various",
};

const CONTINENT_MAP: Record<number, string> = {
  0: "Africa",
  1: "Asia",
  2: "Europe",
  3: "Middle East",
  4: "North America",
  5: "Pacific",
  6: "South America",
  7: "Various",
};

const MARKET_GROUP_MAP: Record<number, string> = {
  0: "Africa",
  1: "Caribbean",
  2: "Central America",
  3: "Eastern Europe",
  4: "Eurozone",
  5: "Japan",
  6: "Middle East",
  7: "Nordics",
  8: "North America",
  9: "North Asia",
  10: "Pacific",
  11: "South America",
  12: "South Asia",
  13: "South East Asia",
  14: "Switzerland",
  15: "Turkey",
  16: "United Kingdom",
  17: "Various",
  18: "Western Europe",
};

const EMU_OECD_ZONE_MAP: Record<number, string> = {
  0: "EMU",
  1: "OECD Ex EMU",
  2: "Other",
};

// Helper function to get enum display value
const getEnumDisplayValue = (
  value: number | null,
  enumMap: Record<number, string>,
): string => {
  if (value === null || value === undefined) return "";
  return enumMap[value] || `Unknown (${value})`;
};

// Definition of the columns
export const country_columns: ColumnDef<Country>[] = [
  {
    accessorKey: "id",
    header: "Id",
  },
  {
    accessorKey: "code",
    header: "Code",
  },
  {
    accessorKey: "economical_zone",
    header: "Economic Zone",
    cell: ({ row }) => {
      const value = row.getValue<number | null>("economical_zone");
      return getEnumDisplayValue(value, ECONOMICAL_ZONE_MAP);
    },
  },
  {
    accessorKey: "market_type",
    header: "Market Type",
    cell: ({ row }) => {
      const value = row.getValue<number | null>("market_type");
      return getEnumDisplayValue(value, MARKET_TYPE_MAP);
    },
  },
  {
    accessorKey: "continent",
    header: "Continent",
    cell: ({ row }) => {
      const value = row.getValue<number | null>("continent");
      return getEnumDisplayValue(value, CONTINENT_MAP);
    },
  },
  {
    accessorKey: "market_group",
    header: "Market Group",
    cell: ({ row }) => {
      const value = row.getValue<number | null>("market_group");
      return getEnumDisplayValue(value, MARKET_GROUP_MAP);
    },
  },
  {
    accessorKey: "en_translation",
    header: "EN Translation",
  },
  {
    accessorKey: "fr_translation",
    header: "FR Translation",
  },
  {
    accessorKey: "currency",
    header: "Currency",
  },
  {
    accessorKey: "emu_oecd_zone",
    header: "EMU OECD Zone",
    cell: ({ row }) => {
      const value = row.getValue<number | null>("emu_oecd_zone");
      return getEnumDisplayValue(value, EMU_OECD_ZONE_MAP);
    },
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => parseDate(row.getValue<string>("created_at")),
  },
  {
    accessorKey: "updated_at",
    header: "Updated At",
    cell: ({ row }) => parseDate(row.getValue<string>("updated_at")),
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => (
      <div className="flex justify-end space-x-2">
        <Link
          href={toWorkspacePath(
            `/admin/qtl - referential/issuers?country_id=${row.getValue("id")}`,
          )}
          className="text-xs"
          underline="hover"
        >
          Issuers
        </Link>
        <Link
          href={`${window.location.pathname}/${row.getValue("id")}`}
          className="text-xs"
          underline="hover"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
